import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject } from "rxjs";
import { AlertController } from "@ionic/angular";
import { PMSTaskRequest } from "../models/PMSTaskRequest";
import { jhcassoAPIUri } from "src/environments/apiUri";
import { environment } from "src/environments/environment";
import { TokenResponse } from "../models/Response/TokenResponse";
import { RefreshTokenRequest } from "../models/Request/RefreshTokenRequest";
import { Geolocation } from "@capacitor/geolocation";

function _window(): any {
  return window;
}

@Injectable({
  providedIn: "root",
})
export class ApiService {

  /* ---------------------------------------------------- */
  /* -------------------- BASE CONFIG -------------------- */
  /* ---------------------------------------------------- */

  BASE_URL = environment.baseUrl;

  /* ---------------------------------------------------- */
  /* -------------------- VARIABLES ---------------------- */
  /* ---------------------------------------------------- */

  paymentData: any;
  addressData: any;
  latitude: string;
  longitude: string;
  deviceToken: string;
  newLogin = new BehaviorSubject<boolean>(false);
  newLang = new BehaviorSubject<boolean>(false);
  total = 0;
  salonId: number;
  refreshTokenRequest: RefreshTokenRequest;
  distance: number;
  date: Date;
  userLat: number;
  userLong: number;
  time: any;
  searchLat: number;
  searchLng: number;
  booking_at: string;
  extraCharges = 0;
  pMSTaskRequest: PMSTaskRequest;
  pmsInfoGroupId = 25;

  constructor(
    private http: HttpClient,
    private alertCtrl: AlertController
  ) { }

  get nativeWindow(): any {
    return _window();
  }

  /* ---------------------------------------------------- */
  /* -------------------- VERSION HANDLER ---------------- */
  /* ---------------------------------------------------- */

  private buildUrl(endpoint: string, version?: string): string {
    const apiVersion = version || environment.defaultApiVersion;
    return `${this.BASE_URL}${apiVersion}/${endpoint}`;
  }

  /* ---------------------------------------------------- */
  /* -------------------- BEHAVIOR ----------------------- */
  /* ---------------------------------------------------- */

  setNewLogin(val: boolean) {
    this.newLogin.next(val);
  }

  isNewLogin() {
    return this.newLogin.asObservable();
  }

  /* ---------------------------------------------------- */
  /* -------------------- UTILITIES ---------------------- */
  /* ---------------------------------------------------- */

  getDistanceFromLatLonInKm(lat1: number, lon1: number, lat2: number, lon2: number) {
    const R = 6371;
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(this.deg2rad(lat1)) *
      Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  deg2rad(deg: number) {
    return deg * (Math.PI / 180);
  }

  async getLocation() {
    try {
      const position = await Geolocation.getCurrentPosition();
      this.latitude = position.coords.latitude.toString();
      this.longitude = position.coords.longitude.toString();
    } catch (err) {
      console.error("Geolocation error:", err);
    }
  }

  async showAlert(title: string, msg: string, task: string) {
    const alert = await this.alertCtrl.create({
      header: title,
      subHeader: msg,
      buttons: [{ text: `Action: ${task}` }],
    });
    await alert.present();
  }

  /* ---------------------------------------------------- */
  /* -------------------- HEADERS ------------------------ */
  /* ---------------------------------------------------- */

  private getAuthHeaders(): HttpHeaders {
    let header = new HttpHeaders();
    header = header.set("Authorization", "Bearer " + localStorage.getItem(environment.xToken));
    header = header.set("Accept", "application/json");
    return header;
  }

  /* ---------------------------------------------------- */
  /* -------------------- GET METHODS -------------------- */
  /* ---------------------------------------------------- */

  get(endpoint: string, version?: string) {
    let header = new HttpHeaders()
      .set("x-jacaso-key", environment.authKey)
      .set("Accept", "application/json");

    return this.http.get(this.buildUrl(endpoint, version), { headers: header });
  }

  getByXKey(endpoint: string, version?: string) {
    let header = new HttpHeaders()
      .set("Authorization", "Bearer " + localStorage.getItem(environment.xToken))
      .set("Accept", "application/json");

    return this.http.get(this.buildUrl(endpoint, version), { headers: header });
  }

  getDataWithToken(endpoint: string, version?: string) {
    return this.http.get(
      this.buildUrl(endpoint, version),
      { headers: this.getAuthHeaders() }
    );
  }

  /* ---------------------------------------------------- */
  /* -------------------- POST METHODS ------------------- */
  /* ---------------------------------------------------- */

  postDataWithToken(endpoint: string, data: any, version?: string) {
    return this.http.post(
      this.buildUrl(endpoint, version),
      data,
      { headers: this.getAuthHeaders() }
    );
  }

  postSearchSalonRequest(endpoint: string, data: any, version?: string) {
    let header = new HttpHeaders()
      .set("x-jacaso-key", environment.authKey)
      .set("Accept", "application/json");

    return this.http.post(
      this.buildUrl(endpoint, version),
      data,
      { headers: header }
    );
  }

  post(endpoint: string, data: any, version?: string) {
    return this.http.post(
      this.buildUrl(endpoint, version),
      data,
      { headers: this.getAuthHeaders() }
    );
  }

  put(endpoint: string, data: any, version?: string) {
    return this.http.put(
      this.buildUrl(endpoint, version),
      data,
      { headers: this.getAuthHeaders() }
    );
  }

  delete(endpoint: string, version?: string) {
    return this.http.delete(
      this.buildUrl(endpoint, version),
      { headers: this.getAuthHeaders() }
    );
  }

  /* ---------------------------------------------------- */
  /* -------------------- OTP METHODS -------------------- */
  /* ---------------------------------------------------- */

  sendOtpRequest(endpoint: string, data: any, version?: string) {
    let headers = new HttpHeaders()
      .set("x-jacaso-key", environment.authKey)
      .set("Accept", "application/json");

    return this.http.post(
      this.buildUrl(endpoint, version),
      data,
      { headers }
    );
  }

  resendOtpRequest(endpoint: string, phone: string, version?: string) {
    let headers = new HttpHeaders()
      .set("x-jacaso-key", environment.authKey)
      .set("Accept", "application/json");

    return this.http.get(
      `${this.buildUrl(endpoint, version)}/${phone}`,
      { headers }
    );
  }

  postVerifyOtpRequest(endpoint: string, data: any, version?: string) {
    let headers = new HttpHeaders()
      .set("x-jacaso-key", environment.authKey)
      .set("Accept", "application/json");

    return this.http.post(
      this.buildUrl(endpoint, version),
      data,
      { headers }
    );
  }

  /* ---------------------------------------------------- */
  /* -------------------- TOKEN MANAGEMENT --------------- */
  /* ---------------------------------------------------- */

  GenerateRefreshToken() {
    this.refreshTokenRequest = new RefreshTokenRequest(
      this.GetToken(),
      this.GetRefreshToken()
    );

    let header = new HttpHeaders()
      .set("x-jacaso-key", environment.authKey)
      .set("Accept", "application/json");

    return this.http.post(
      this.buildUrl(jhcassoAPIUri.refreshToken),
      this.refreshTokenRequest,
      { headers: header }
    );
  }

  isLoggedIn() {
    return !!(localStorage.getItem(environment.xToken));
  }

  GetToken() {
    return localStorage.getItem(environment.xToken) || "";
  }

  GetRefreshToken() {
    return localStorage.getItem(environment.xrefreshToken) || "";
  }

  SaveTokens(tokendata: TokenResponse) {
    localStorage.setItem(environment.xToken, tokendata.token);
    localStorage.setItem(environment.xrefreshToken, tokendata.refreshToken);
  }

  onLogOut() {
    localStorage.removeItem(environment.xToken);
    localStorage.removeItem(environment.xrefreshToken);
  }
}